import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bookmark, BookmarkCheck, EyeOff, ExternalLink, HelpCircle, Flag,
  ShoppingCart, Clock, Loader2, ChefHat, Tag, MapPin, Lock, Pin, CalendarPlus,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* Shared feed cards for the SocialPulse Content & Recommendation Service.
   Every card: title, source, dates, why-this, Save/Hide/Report, source link.
   Recipes get the reviewed "add missing ingredients to shopping list" flow. */

export function useFeed(module, type, source) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false); // false | true | "premium"
  const load = useCallback(() => {
    api.get("/pulse/feed", { params: { module, type: type || undefined, source: source || undefined } })
      .then((r) => { setItems(r.data.items); setError(false); })
      .catch((e) => { setItems([]); setError(e.response?.status === 402 ? "premium" : true); });
  }, [module, type, source]);
  useEffect(() => { load(); }, [load]);
  return { items, error, reload: load, setItems };
}

export function PremiumUpsell({ feature }) {
  return (
    <div className="border border-amber-300 bg-amber-50 rounded-2xl px-5 py-6 text-center" data-testid="premium-upsell">
      <Lock className="w-6 h-6 text-amber-600 mx-auto" />
      <p className="mt-2 text-sm font-700 text-zinc-800">{feature} is part of your Hub membership</p>
      <p className="text-xs text-zinc-600 mt-1">Your free trial has ended. Renew to keep {feature.toLowerCase()} — your data is safe and waiting.</p>
      <Link to="/membership" data-testid="premium-upsell-cta"
        className="inline-block mt-3 bg-blue-700 text-white text-sm font-700 px-5 py-2.5 rounded-full hover:bg-blue-800">See membership options</Link>
    </div>
  );
}

export function FeedCard({ item, onChanged, trip }) {
  const [why, setWhy] = useState(false);
  const [review, setReview] = useState(null); // shopping review dialog state
  const [busy, setBusy] = useState(false);
  const [day, setDay] = useState(1);

  const feedback = async (action) => {
    try {
      await api.post(`/pulse/items/${encodeURIComponent(item.gid)}/feedback`, { action });
      if (action === "report") toast.success("Reported — thank you, we will review it");
      if (action === "hide") toast.success("Hidden — you won't see this again");
      onChanged?.(action, item);
    } catch { toast.error("Action failed"); }
  };

  const openReview = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/pulse/items/${encodeURIComponent(item.gid)}/shopping-review`);
      setReview({ ...r.data, selected: new Set(r.data.missing) });
    } catch { toast.error("Could not check ingredients"); }
    setBusy(false);
  };

  const addSelected = async () => {
    const ingredients = [...review.selected];
    if (ingredients.length === 0) { setReview(null); return; }
    try {
      const r = await api.post(`/pulse/items/${encodeURIComponent(item.gid)}/add-to-shopping`, { ingredients });
      toast.success(`${r.data.added.length} added to shopping list${r.data.skipped.length ? `, ${r.data.skipped.length} already on it` : ""}`);
      setReview(null);
    } catch { toast.error("Could not add"); }
  };

  const d = item.data || {};

  const saveToTrip = async () => {
    try {
      const action = item.saved_to_trip ? "unsave-from-trip" : "save-to-trip";
      await api.post(`/pulse/items/${encodeURIComponent(item.gid)}/${action}`, { trip_id: trip.id });
      toast.success(item.saved_to_trip ? "Removed from trip" : "Saved to this trip");
      onChanged?.(action, item);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update trip"); }
  };

  const addToPlan = async () => {
    try {
      const r = await api.post(`/pulse/items/${encodeURIComponent(item.gid)}/add-to-trip-activity`, { trip_id: trip.id, day });
      toast.success(r.data.duplicate ? `Already on Day ${r.data.day}'s plan` : `Added as an editable proposal to Day ${r.data.day} — not a booking`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };

  return (
    <article className="bg-white border border-zinc-200 rounded-2xl p-4 flex flex-col gap-2" data-testid={`feed-card-${item.gid}`}>
      <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wide text-zinc-500">
        <span className="text-blue-700 font-700">{item.source_name}</span>
        {item.sponsored && <span className="bg-amber-100 text-amber-800 border border-amber-300 rounded px-1.5 py-0.5 font-800">Sponsored</span>}
        {item.fixture && <span className="bg-zinc-100 text-zinc-500 border border-zinc-300 rounded px-1.5 py-0.5">Sample</span>}
        {item.place && <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{item.place}</span>}
        {item.expires_at && (
          <span className="inline-flex items-center gap-0.5 text-amber-700"><Clock className="w-3 h-3" />
            ends {new Date(item.expires_at).toLocaleDateString()}
          </span>
        )}
      </div>

      {item.image_url && (
        <a href={item.url} target="_blank" rel="noreferrer" className="block -mt-0.5">
          <img src={item.image_url} alt="" loading="lazy" data-testid={`feed-img-${item.gid}`}
            className="w-full h-36 object-cover rounded-xl border border-zinc-100" />
        </a>
      )}

      <a href={item.url} target="_blank" rel="noreferrer" className="font-heading font-800 text-base leading-snug text-zinc-900 hover:text-blue-700 transition-colors" data-testid={`feed-title-${item.gid}`}>
        {item.title}
      </a>
      {item.summary && <p className="text-sm text-zinc-600">{item.summary}</p>}

      {item.type === "offer" && d.merchant && (
        <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2">
          <b className="text-zinc-700">{d.merchant}</b>{d.price ? ` · ${d.price}` : ""}{d.terms ? ` · ${d.terms}` : ""}
          <div className="mt-0.5">Verified {item.verified_at ? new Date(item.verified_at).toLocaleDateString() : "—"} — availability is confirmed by the seller when you open the offer.</div>
        </div>
      )}
      {item.type === "recipe" && d.ingredients && (
        <div className="text-xs text-zinc-500 inline-flex items-center gap-1.5 flex-wrap">
          <ChefHat className="w-3.5 h-3.5 text-emerald-700" />
          {d.ingredients.slice(0, 6).join(", ")}{d.ingredients.length > 6 ? "…" : ""}
          {d.prep_minutes ? <span className="text-zinc-400">· {d.prep_minutes} min</span> : null}
        </div>
      )}

      {why && (
        <div className="text-xs bg-sky-50 border border-sky-200 text-sky-800 rounded-lg px-3 py-2" data-testid={`feed-why-${item.gid}`}>
          {item.why} <button onClick={() => setWhy(false)} className="underline ml-1">Close</button>
        </div>
      )}

      <div className="flex items-center gap-1 mt-auto pt-1 flex-wrap">
        <a href={item.url} target="_blank" rel="noreferrer" data-testid={`feed-open-${item.gid}`}
          className="inline-flex items-center gap-1 text-xs font-700 text-blue-700 border border-blue-200 bg-blue-50 rounded-lg px-2.5 py-1.5 hover:bg-blue-100">
          <ExternalLink className="w-3.5 h-3.5" /> Open source
        </a>
        {item.type === "recipe" && (
          <button onClick={openReview} disabled={busy} data-testid={`feed-ingredients-${item.gid}`}
            className="inline-flex items-center gap-1 text-xs font-700 text-emerald-700 border border-emerald-300 bg-emerald-50 rounded-lg px-2.5 py-1.5 hover:bg-emerald-100">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShoppingCart className="w-3.5 h-3.5" />} Check ingredients
          </button>
        )}
        {trip && (
          <>
            <button onClick={saveToTrip} data-testid={`feed-savetrip-${item.gid}`}
              className={`inline-flex items-center gap-1 text-xs font-700 rounded-lg px-2.5 py-1.5 border ${item.saved_to_trip ? "bg-violet-600 text-white border-violet-600" : "text-violet-700 border-violet-300 bg-violet-50 hover:bg-violet-100"}`}>
              <Pin className="w-3.5 h-3.5" /> {item.saved_to_trip ? "Saved to trip" : "Save to trip"}
            </button>
            <span className="inline-flex items-center gap-1">
              <select value={day} onChange={(e) => setDay(Number(e.target.value))} data-testid={`feed-day-${item.gid}`}
                className="notranslate text-xs border border-zinc-300 rounded-lg px-1 py-1.5 bg-white">
                {Array.from({ length: Math.max(1, Math.min(trip.days || 1, 30)) }, (_, i) => i + 1)
                  .map((n) => <option key={n} value={n}>Day {n}</option>)}
              </select>
              <button onClick={addToPlan} data-testid={`feed-addplan-${item.gid}`}
                className="inline-flex items-center gap-1 text-xs font-700 text-blue-700 border border-blue-200 bg-blue-50 rounded-lg px-2.5 py-1.5 hover:bg-blue-100">
                <CalendarPlus className="w-3.5 h-3.5" /> Add to plan
              </button>
            </span>
          </>
        )}
        <span className="ml-auto flex items-center gap-0.5">
          <button onClick={() => feedback(item.saved ? "unsave" : "save")} title={item.saved ? "Unsave" : "Save"} data-testid={`feed-save-${item.gid}`}
            className={`p-1.5 rounded-lg transition-colors ${item.saved ? "text-blue-700" : "text-zinc-400 hover:text-blue-700"}`}>
            {item.saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
          </button>
          <button onClick={() => setWhy((v) => !v)} title="Why am I seeing this?" data-testid={`feed-whybtn-${item.gid}`} className="p-1.5 rounded-lg text-zinc-400 hover:text-sky-700"><HelpCircle className="w-4 h-4" /></button>
          <button onClick={() => feedback("hide")} title="Hide / show less" data-testid={`feed-hide-${item.gid}`} className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700"><EyeOff className="w-4 h-4" /></button>
          <button onClick={() => feedback("report")} title="Report" data-testid={`feed-report-${item.gid}`} className="p-1.5 rounded-lg text-zinc-400 hover:text-rose-600"><Flag className="w-4 h-4" /></button>
        </span>
      </div>

      {review && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={() => setReview(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ingredient-review-dialog">
            <h3 className="font-heading font-800 text-lg">Review ingredients</h3>
            <p className="text-xs text-zinc-500 mt-0.5">{review.note}</p>
            {review.have.length > 0 && (
              <div className="mt-3 text-sm"><b className="text-emerald-700">In your pantry:</b> {review.have.map((h) => h.ingredient).join(", ")}</div>
            )}
            {review.on_list.length > 0 && (
              <div className="mt-2 text-sm"><b className="text-blue-700">Already on the list:</b> {review.on_list.join(", ")}</div>
            )}
            <div className="mt-3">
              <b className="text-sm">Missing — select what to add:</b>
              {review.missing.length === 0 ? <p className="text-sm text-zinc-500 mt-1">Nothing missing.</p> : (
                <ul className="mt-1.5 space-y-1">
                  {review.missing.map((m) => (
                    <li key={m}>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" className="w-4 h-4 accent-emerald-700" checked={review.selected.has(m)}
                          onChange={() => setReview((r) => { const s = new Set(r.selected); s.has(m) ? s.delete(m) : s.add(m); return { ...r, selected: s }; })}
                          data-testid={`review-check-${m.replace(/\s+/g, "-")}`} />
                        {m}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setReview(null)} className="px-4 py-2 text-sm font-700 border border-zinc-300 rounded-lg" data-testid="review-cancel">Cancel</button>
              <button onClick={addSelected} disabled={review.selected.size === 0} data-testid="review-add-btn"
                className="px-4 py-2 text-sm font-700 bg-emerald-700 text-white rounded-lg disabled:opacity-40">Add {review.selected.size} to shopping list</button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

export function FeedGrid({ module, type, source, emptyText }) {
  const { items, error, setItems } = useFeed(module, type, source);
  if (!items) return <p className="text-sm text-zinc-500 py-6 text-center">Loading suggestions…</p>;
  if (error === "premium") return <PremiumUpsell feature="Personalized recommendations" />;
  if (error) return <p className="text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-300 rounded-xl">Suggestions are unavailable right now — your own lists still work.</p>;
  if (items.length === 0) return <p className="text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-300 rounded-xl" data-testid="feed-empty">{emptyText || "No suggestions right now — check back soon."}</p>;
  const onChanged = (action, it) => {
    if (action === "hide") setItems((arr) => arr.filter((x) => x.gid !== it.gid));
    if (action === "save" || action === "unsave") setItems((arr) => arr.map((x) => x.gid === it.gid ? { ...x, saved: action === "save" } : x));
  };
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid={`feed-grid-${source || module}${type ? `-${type}` : ""}`}>
      {items.map((it) => <FeedCard key={it.gid} item={it} onChanged={onChanged} />)}
    </div>
  );
}

export function InterestChips() {
  const [prefs, setPrefs] = useState(null);
  useEffect(() => { api.get("/pulse/preferences").then((r) => setPrefs(r.data)).catch(() => setPrefs({ interests: [] })); }, []);
  if (!prefs) return null;
  const OPTIONS = ["kerala", "india", "vegetarian", "quick", "breakfast", "deals", "guides", "packing", "transport", "tips"];
  const toggle = (t) => {
    const interests = prefs.interests.includes(t) ? prefs.interests.filter((x) => x !== t) : [...prefs.interests, t];
    setPrefs((p) => ({ ...p, interests }));
    api.put("/pulse/preferences", { ...prefs, interests }).catch(() => {});
  };
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="interest-chips">
      <span className="text-xs font-mono uppercase text-zinc-500 inline-flex items-center gap-1"><Tag className="w-3 h-3" /> Interests:</span>
      {OPTIONS.map((t) => (
        <button key={t} onClick={() => toggle(t)} data-testid={`interest-${t}`}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${prefs.interests.includes(t) ? "bg-blue-700 text-white border-blue-700" : "bg-white text-zinc-600 border-zinc-300 hover:border-blue-500"}`}>
          {t}
        </button>
      ))}
    </div>
  );
}
